import React, { useState, useMemo, useEffect } from 'react';
import { type Event } from 'nostr-tools';
import {
  Heart, Star, Target, Coins, Film, Music, FileText,
  CalendarDays, Rocket, Sprout, LogIn, Loader2, Sparkles,
} from 'lucide-react';
import { useAppContext } from '../hooks/useAppContext';
import { useNostrQuery } from '../hooks/useNostrQuery';
import { useBCHPrice } from '../hooks/useBCHPrice';
import SupportSomeoneNew from '../components/SupportSomeoneNew';
import {
  MEMBERSHIP_KIND, MEMBERSHIP_TAG,
  GOAL_KIND, GOAL_TAG, GOAL_PLEDGE_KIND, GOAL_PLEDGE_TAG,
  currentMonth,
} from '../lib/creatorSupport';

// ── Kinds & Tags ────────────────────────────────────────────────────
const TIP_RECEIPT_KIND = 9735;
const MEMBERSHIP_SUB_KIND = 9735;
const MEMBERSHIP_SUB_TAG = 'bch-membership-sub';
const VIDEO_KINDS = [22, 21, 34236, 34235];
const ARTICLE_KIND = 30023;
const RADIO_TRACK_KIND = 1808;
const RADIO_TRACK_TAG = 'bch-radio-track';
const DATE_EVENT_KIND = 31922;
const TIME_EVENT_KIND = 31923;
const BOOST_KIND = 30078;
const BOOST_TAG = 'bch-boost';

// ── Helper: extract sats from a tip event ───────────────────────────
function extractTipSats(ev: Event): number {
  let sats = 0;
  try {
    const amountTag = ev.tags?.find((t: string[]) => t[0] === 'amount')?.[1];
    if (amountTag) sats = parseInt(amountTag, 10) || 0;
    if (!sats) {
      const content = JSON.parse(ev.content || '{}');
      sats = content.amount_sats || 0;
    }
  } catch {}
  return sats;
}

// ── Stat Card ───────────────────────────────────────────────────────
function ImpactStat({ label, value, sub, icon: Icon, color, emoji }: {
  label: string;
  value: string | number;
  sub?: string;
  icon: React.ElementType;
  color: string;
  emoji?: string;
}) {
  return (
    <div className="rounded-xl border border-border bg-card p-3.5 space-y-1">
      <div className="flex items-center gap-2">
        <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${color}15` }}>
          {emoji
            ? <span className="text-sm">{emoji}</span>
            : <Icon className="w-3.5 h-3.5" style={{ color }} />}
        </div>
        <span className="text-[11px] text-muted-foreground font-medium leading-tight">{label}</span>
      </div>
      <p className="text-xl font-bold tabular-nums leading-tight">{typeof value === 'number' ? value.toLocaleString() : value}</p>
      {sub && <p className="text-[10px] text-muted-foreground">{sub}</p>}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// ██  MY IMPACT PAGE
// ══════════════════════════════════════════════════════════════════════

export default function MyImpactPage() {
  const { user, setShowLoginDialog } = useAppContext();
  const { price: bchPrice } = useBCHPrice();

  // ── Guard: must be logged in ──
  if (!user) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="sticky top-0 z-10 bg-background/90 backdrop-blur-md border-b border-border px-4 py-3">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center shadow-lg shadow-emerald-500/20">
              <span className="text-lg">💚</span>
            </div>
            <div>
              <h1 className="text-base font-bold">My Impact</h1>
              <p className="text-[10px] text-muted-foreground">Your creator economy contributions</p>
            </div>
          </div>
        </div>
        <div className="flex flex-col items-center justify-center py-20 px-6 text-center">
          <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mb-4">
            <LogIn className="w-8 h-8 text-muted-foreground" />
          </div>
          <h2 className="text-lg font-bold mb-2">Log in to see your impact</h2>
          <p className="text-sm text-muted-foreground mb-6 max-w-xs">
            See how your tips, memberships, and contributions help grow the BCH creator economy.
          </p>
          <button
            onClick={() => setShowLoginDialog(true)}
            className="px-6 py-3 rounded-xl bg-primary text-primary-foreground font-bold text-sm hover:bg-primary/90 active:scale-95 transition-all"
          >
            Log In or Sign Up
          </button>
        </div>
      </div>
    );
  }

  return <ImpactDashboard pubkey={user.pubkey} />;
}

// ── Main Dashboard (only rendered when logged in) ───────────────────
function ImpactDashboard({ pubkey }: { pubkey: string }) {
  const { price: bchPrice } = useBCHPrice();

  // Time boundaries
  const allTimeSince = useMemo(() => Math.floor(Date.now() / 1000) - 365 * 86400, []);

  // ── Data queries ───────────────────────────────────────────────────
  // All tips SENT by this user
  const { events: tipsSent, loading: tipsLoading } = useNostrQuery({
    filters: [{ kinds: [TIP_RECEIPT_KIND], authors: [pubkey], '#t': ['bch-tip'], limit: 1000, since: allTimeSince }],
    allRelays: true,
  });

  // Membership subscriptions PAID by this user
  const { events: membershipPayments } = useNostrQuery({
    filters: [{ kinds: [MEMBERSHIP_SUB_KIND], authors: [pubkey], '#t': [MEMBERSHIP_SUB_TAG], limit: 200, since: allTimeSince }],
    allRelays: true,
  });

  // Goal pledges MADE by this user
  const { events: goalPledges } = useNostrQuery({
    filters: [{ kinds: [GOAL_PLEDGE_KIND], authors: [pubkey], '#t': [GOAL_PLEDGE_TAG], limit: 200, since: allTimeSince }],
    allRelays: true,
  });

  // Boosts PURCHASED by this user
  const { events: boostsPurchased } = useNostrQuery({
    filters: [{ kinds: [BOOST_KIND], authors: [pubkey], '#t': [BOOST_TAG], limit: 200, since: allTimeSince }],
    allRelays: true,
  });

  // ── Resolve tipped content kinds ──────────────────────────────────
  // We need to know WHAT was tipped to categorize clips, songs, articles, events
  const tippedEventIds = useMemo(() => {
    const ids = new Set<string>();
    for (const ev of tipsSent) {
      const eid = ev.tags?.find((t: string[]) => t[0] === 'e')?.[1];
      if (eid) ids.add(eid);
    }
    return [...ids];
  }, [tipsSent]);

  // Fetch the tipped events to inspect their kind
  const { events: tippedEvents } = useNostrQuery({
    filters: tippedEventIds.length > 0 ? [{ ids: tippedEventIds.slice(0, 300), limit: 300 }] : [],
    enabled: tippedEventIds.length > 0,
    allRelays: true,
  });

  // Build a map: eventId → kind
  const tippedKindMap = useMemo(() => {
    const map = new Map<string, number>();
    for (const ev of tippedEvents) map.set(ev.id, ev.kind);
    return map;
  }, [tippedEvents]);

  // ══════════════════════════════════════════════════════════════════
  //  COMPUTED STATS
  // ══════════════════════════════════════════════════════════════════

  // ── Creators Supported ───────────────────────────────────────────
  const supportedCreators = useMemo(() => {
    const pks = new Set<string>();
    for (const ev of tipsSent) {
      const p = ev.tags?.find((t: string[]) => t[0] === 'p')?.[1];
      if (p && p !== pubkey) pks.add(p);
    }
    for (const ev of membershipPayments) {
      const p = ev.tags?.find((t: string[]) => t[0] === 'p')?.[1];
      if (p && p !== pubkey) pks.add(p);
    }
    for (const ev of goalPledges) {
      const p = ev.tags?.find((t: string[]) => t[0] === 'p')?.[1];
      if (p && p !== pubkey) pks.add(p);
    }
    return pks;
  }, [tipsSent, membershipPayments, goalPledges, pubkey]);

  // ── New Creators Discovered ──────────────────────────────────────
  // Compare against the locally-tracked set
  const newCreatorsDiscovered = useMemo(() => {
    return supportedCreators.size;
  }, [supportedCreators]);

  // ── Active Memberships ───────────────────────────────────────────
  const activeMemberships = useMemo(() => {
    const thisMonth = currentMonth();
    const active = new Set<string>();
    for (const ev of membershipPayments) {
      const month = ev.tags?.find((t: string[]) => t[0] === 'month')?.[1];
      const p = ev.tags?.find((t: string[]) => t[0] === 'p')?.[1];
      if (month === thisMonth && p) active.add(p);
    }
    return active.size;
  }, [membershipPayments]);

  // ── Creator Goals Supported ──────────────────────────────────────
  const goalsSupported = useMemo(() => {
    const goalIds = new Set<string>();
    for (const ev of goalPledges) {
      const eid = ev.tags?.find((t: string[]) => t[0] === 'e')?.[1];
      if (eid) goalIds.add(eid);
    }
    return goalIds.size;
  }, [goalPledges]);

  // ── Content-type tip breakdowns ──────────────────────────────────
  const contentStats = useMemo(() => {
    let clipsTipped = 0;
    let songsTipped = 0;
    let articlesTipped = 0;
    let eventsTipped = 0;

    for (const ev of tipsSent) {
      const eid = ev.tags?.find((t: string[]) => t[0] === 'e')?.[1];
      if (!eid) continue;
      const kind = tippedKindMap.get(eid);
      if (!kind) continue;
      if (VIDEO_KINDS.includes(kind)) clipsTipped++;
      else if (kind === RADIO_TRACK_KIND) songsTipped++;
      else if (kind === ARTICLE_KIND) articlesTipped++;
      else if (kind === DATE_EVENT_KIND || kind === TIME_EVENT_KIND) eventsTipped++;
    }

    // Also count tips that have content-specific tags as fallback
    for (const ev of tipsSent) {
      const tags = ev.tags?.map((t: string[]) => t[0] === 't' ? t[1] : null).filter(Boolean) || [];
      if (tags.includes('superchat')) continue; // already counted as live tip
    }

    return { clipsTipped, songsTipped, articlesTipped, eventsTipped };
  }, [tipsSent, tippedKindMap]);

  // ── Total BCH Contributed ────────────────────────────────────────
  const totalContributed = useMemo(() => {
    let total = 0;
    // Tips
    for (const ev of tipsSent) {
      total += extractTipSats(ev);
    }
    // Memberships
    for (const ev of membershipPayments) {
      const amt = ev.tags?.find((t: string[]) => t[0] === 'amount')?.[1];
      if (amt) total += parseInt(amt, 10) || 0;
    }
    // Goal pledges
    for (const ev of goalPledges) {
      const amt = ev.tags?.find((t: string[]) => t[0] === 'amount')?.[1];
      if (amt) total += parseInt(amt, 10) || 0;
    }
    // Boosts
    for (const ev of boostsPurchased) {
      try {
        const data = JSON.parse(ev.content || '{}');
        total += data.bidSats || data.amountSats || 0;
      } catch {
        const amt = ev.tags?.find((t: string[]) => t[0] === 'amount')?.[1];
        if (amt) total += parseInt(amt, 10) || 0;
      }
    }
    return total;
  }, [tipsSent, membershipPayments, goalPledges, boostsPurchased]);

  // Format BCH
  const fmtBch = (sats: number): string => {
    if (sats === 0) return '—';
    const bch = sats / 1e8;
    if (bchPrice) {
      const usd = bch * bchPrice;
      if (usd >= 0.01) return `$${usd.toFixed(2)}`;
    }
    if (sats >= 1_000_000) return `${(sats / 1_000_000).toFixed(2)}M sats`;
    if (sats >= 1_000) return `${(sats / 1_000).toFixed(1)}k sats`;
    return `${sats.toLocaleString()} sats`;
  };

  const isLoading = tipsLoading;

  // ══════════════════════════════════════════════════════════════════
  //  RENDER
  // ══════════════════════════════════════════════════════════════════

  return (
    <div className="max-w-2xl mx-auto">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background/90 backdrop-blur-md border-b border-border px-4 py-3">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center shadow-lg shadow-emerald-500/20">
            <span className="text-lg">💚</span>
          </div>
          <div>
            <h1 className="text-base font-bold">My Impact</h1>
            <p className="text-[10px] text-muted-foreground">Your creator economy contributions</p>
          </div>
        </div>
      </div>

      {/* Intro message */}
      <div className="px-4 pt-4 pb-2">
        <div className="rounded-2xl bg-gradient-to-r from-emerald-500/5 via-primary/5 to-green-500/5 border border-emerald-500/15 p-4">
          <p className="text-sm text-foreground/80 leading-relaxed">
            <span className="text-base mr-1">💚</span>
            Every tip, membership, and contribution helps grow the Bitcoin Cash creator economy.
          </p>
        </div>
      </div>

      {isLoading && supportedCreators.size === 0 ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="px-4 py-3 space-y-5">

          {/* ── Hero Stat: Total BCH Contributed ── */}
          <div className="rounded-2xl border-2 border-emerald-500/20 bg-gradient-to-b from-emerald-500/5 to-transparent p-5 text-center space-y-1">
            <p className="text-[10px] text-muted-foreground font-semibold uppercase tracking-wider">Total BCH Contributed</p>
            <p className="text-3xl font-extrabold text-emerald-500 tabular-nums">{fmtBch(totalContributed)}</p>
            {totalContributed > 0 && bchPrice && (
              <p className="text-xs text-muted-foreground">{(totalContributed / 1e8).toFixed(4)} BCH</p>
            )}
            {totalContributed === 0 && (
              <p className="text-xs text-muted-foreground">Start supporting creators to see your impact grow</p>
            )}
          </div>

          {/* ── Core Impact Stats ── */}
          <div className="grid grid-cols-2 gap-2.5">
            <ImpactStat
              label="Creators Supported"
              value={supportedCreators.size}
              icon={Heart}
              color="#10B981"
              emoji="💚"
              sub={supportedCreators.size === 0 ? 'No data yet' : 'unique creators'}
            />
            <ImpactStat
              label="New Creators Discovered"
              value={newCreatorsDiscovered}
              icon={Sprout}
              color="#22C55E"
              emoji="🌱"
              sub={newCreatorsDiscovered === 0 ? 'No data yet' : 'first-time support'}
            />
            <ImpactStat
              label="Active Memberships"
              value={activeMemberships}
              icon={Star}
              color="#F59E0B"
              emoji="⭐"
              sub={activeMemberships === 0 ? 'No data yet' : 'this month'}
            />
            <ImpactStat
              label="Goals Supported"
              value={goalsSupported}
              icon={Target}
              color="#6366F1"
              emoji="🎯"
              sub={goalsSupported === 0 ? 'No data yet' : 'creator goals'}
            />
            <ImpactStat
              label="Boosts Purchased"
              value={boostsPurchased.length}
              icon={Rocket}
              color="#3B82F6"
              emoji="🚀"
              sub={boostsPurchased.length === 0 ? 'No data yet' : undefined}
            />
            <ImpactStat
              label="Tips Sent"
              value={tipsSent.length}
              icon={Coins}
              color="#10B981"
              sub={tipsSent.length === 0 ? 'No data yet' : `across ${supportedCreators.size} creators`}
            />
          </div>

          {/* ── Content-Specific Support ── */}
          <div>
            <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5" /> Content You've Supported
            </p>
            <div className="grid grid-cols-2 gap-2.5">
              <ImpactStat
                label="Cash Clips Tipped"
                value={contentStats.clipsTipped}
                icon={Film}
                color="#F59E0B"
                emoji="\uD83C\uDFA5"
                sub={contentStats.clipsTipped === 0 ? 'No data yet' : undefined}
              />
              <ImpactStat
                label="Songs Supported"
                value={contentStats.songsTipped}
                icon={Music}
                color="#EF4444"
                emoji="\uD83C\uDFB5"
                sub={contentStats.songsTipped === 0 ? 'No data yet' : undefined}
              />
              <ImpactStat
                label="Publications Supported"
                value={contentStats.articlesTipped}
                icon={FileText}
                color="#8B5CF6"
                emoji="\uD83D\uDCDA"
                sub={contentStats.articlesTipped === 0 ? 'No data yet' : undefined}
              />
              <ImpactStat
                label="Events Supported"
                value={contentStats.eventsTipped}
                icon={CalendarDays}
                color="#10B981"
                emoji="\uD83D\uDCC5"
                sub={contentStats.eventsTipped === 0 ? 'No data yet' : undefined}
              />
            </div>
          </div>

          {/* ── Discover Another Creator ── */}
          <SupportSomeoneNew />

          {/* Footer */}
          <div className="text-center pb-4 pt-2">
            <p className="text-[10px] text-muted-foreground/40">
              Data sourced from Nostr relays. Numbers may vary based on relay availability.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

